'use client';

import { isOnFullScreenPath } from '@baseball-near-you/common';
import { Button, Form, FormInput, Separator, cn } from '@baseball-near-you/ui';
import { PATHS } from '@bb/lib/constants';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { createQueryString } from '@bb/lib/utils/createQueryString';
import { SiInstagram, SiX } from '@icons-pack/react-simple-icons';
import { Check, ChevronRight, Copyright } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { LoginModal } from './Auth/LoginModal/LoginModal';
import { useEmailListSubscriptionMutation } from './Blog/EmailSubscriptionForm';
import { Branding } from './Branding';
import { Container } from './Container';

export enum FooterTabs {
  Services = 'Services',
  Teams = 'Teams',
  Training = 'Training',
  Mission = 'Mission',
  Blog = 'Blog',
  About = 'About',
  OurStory = 'Our Story',
  Benefits = 'Benefits',
  Careers = 'Careers',
  Help = 'Help',
  FAQs = 'FAQs',
  ContactUs = 'Contact Us',
  Advertise = 'Advertise',
}

export function Footer(props: { links: { label: string; slug: string }[] }) {
  const pathname = usePathname();
  const { userProfile } = useUserProfile();
  const posthog = usePostHog();

  return isOnFullScreenPath(pathname, [PATHS.Settings]) ? null : (
    <Container className="mt-auto pb-6">
      <div className="flex w-full justify-between pt-8">
        <Branding />
        {!userProfile?.id && (
          <div className="hidden items-center space-x-6 md:flex">
            <p className="title text-gray-500">Ready to get started?</p>
            <LoginModal
              modalTrigger={({ open, setOpen }) => (
                <Button onClick={() => setOpen(!open)}>Join Club</Button>
              )}
            />
          </div>
        )}
      </div>
      <Separator className="my-6" />
      <div className="mb-16 flex h-full flex-col justify-between space-y-5 md:flex-row md:space-y-0">
        <EmailSubscriptionForm />
        <Separator orientation="vertical" />
        <div className="grid grid-flow-row grid-cols-2 gap-x-4 gap-y-4 md:ml-6 md:grid-flow-col md:grid-rows-5 md:gap-x-16 md:gap-y-4 lg:ml-0 lg:grid-cols-3">
          {props.links.map((link) => (
            <Link href={`/${link.slug}`} key={link.label} prefetch={false}>
              <p className="title">{link.label}</p>
            </Link>
          ))}
        </div>
      </div>
      <div className="flex w-full justify-between text-gray-600">
        <div className="flex items-center">
          <Copyright size={13} />
          <p className="title ml-1">
            {new Date().getFullYear()} - baseballnearyou.com
          </p>
        </div>
        <div className="flex space-x-4">
          {/* <a target="_blank" href="https://x.com/baseballnearyou" className='hover:scale-105 transition-transform'>
            <SiFacebook />
          </a> */}
          <a
            target="_blank"
            href="https://www.instagram.com/baseballnearyou"
            className="transition-transform hover:scale-105"
            onClick={() => {
              posthog.capture('footer_social_link_clicked', {
                social_media: 'instagram',
              });
            }}
            rel="noreferrer"
          >
            <SiInstagram />
          </a>
          <a
            target="_blank"
            href="https://x.com/baseballnearyou"
            className="transition-transform hover:scale-105"
            onClick={() => {
              posthog.capture('footer_social_link_clicked', {
                social_media: 'x',
              });
            }}
            rel="noreferrer"
          >
            <SiX />
          </a>
        </div>
      </div>
    </Container>
  );
}

export function EmailSubscriptionForm() {
  const { userProfile } = useUserProfile();
  const { handleSubscribeToEmailList, isSubscribingToEmailList } =
    useEmailListSubscriptionMutation();
  const [subscribed, setSubscribed] = useState(false);
  return (
    <div>
      <div className="mb-3 font-semibold text-2xl">
        Subscribe to our newsletter
      </div>
      <Form
        onSubmit={async ({ email }) => {
          await handleSubscribeToEmailList(email);
          setSubscribed(true);
        }}
        schema={z.object({
          email: z.string().email().min(1),
        })}
        initialValues={{
          email: userProfile?.email ?? '',
        }}
        className="relative flex w-full"
      >
        <FormInput
          name="email"
          label=""
          containerClassName="w-full z-10"
          placeholder="Enter your email address"
        />
        <EmailSubscriptionFormButton subscribed={subscribed} />
      </Form>
    </div>
  );
}

export function EmailSubscriptionFormButton({
  subscribed,
}: { subscribed: boolean }) {
  const { formState, watch } = useFormContext();
  const searchParams = useSearchParams();
  const email = watch('email');

  return (
    <>
      <Button
        type="submit"
        className={cn(
          'mt-0.5 ml-2 transition-colors',
          subscribed && 'bg-green-600'
        )}
        loading={formState.isSubmitting}
      >
        {subscribed ? <Check size={15} /> : <ChevronRight size={15} />}
      </Button>
      <Link
        href={`${PATHS.Onboarding}?${createQueryString(
          'email',
          email,
          searchParams
        )}`}
        className={cn(
          'absolute top-0 left-0 mt-0 rounded-md border bg-gray-100 p-1 opacity-0 transition-all hover:bg-gray-200',
          !!email && subscribed && 'top-full mt-2 opacity-100'
        )}
      >
        <p className="w-64 text-blue-500 text-sm">
          Next, click here to create an account, customize your profile and
          review teams.
        </p>
      </Link>
    </>
  );
}
